<template>
  <!-- No visible UI - service component -->
  <div style="display: none;"></div>
</template>

<script>
export default {
  name: "AISummaryCopilot",
  props: {
    serverUrl: {
      type: String,
      default: process.env.VUE_APP_SOCKET_URL
    },
    projectId: {
      type: [Number, String],
      default: null
    },
    talentId: {
      type: [Number, String],
      default: null
    },
    localStorageUserIdKey: {
      type: String,
      default: "user_id"
    },
    localStorageTokenKey: {
      type: String,
      default: "token"
    }
  },
  data() {
    return {
      socket: null,
      connectionStatus: "disconnected",
      isConnected: false,
      isProcessing: false,
      userId: null
    };
  },
  watch: {
    talentId: {
      handler(newVal) {
        console.log("AISummaryCopilot: talentId changed to", newVal);
      }
    }
  },
  created() {
    // Get user ID from localStorage when component is created
    this.getUserIdFromLocalStorage();
  },
  beforeUnmount() {
    // Disconnect WebSocket when component is unmounted
    this.disconnect();
  },
  methods: {
    formatRawResponse(rawText) {
      const boldText = rawText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      
      const htmlFormatted = boldText.replace(/\n/g, '<br>');
      
      return htmlFormatted;
    },
    // Get user ID from localStorage
    getUserIdFromLocalStorage() {
      try {
        const rawUserData = localStorage.getItem('user');
        if (!rawUserData) {
          console.warn('No user data found in localStorage');
          return;
        }

        const jsonStartIndex = rawUserData.indexOf('{');
        const jsonEndIndex = rawUserData.lastIndexOf('}') + 1;
        const jsonPart = rawUserData.substring(jsonStartIndex, jsonEndIndex);

        const userData = JSON.parse(jsonPart);
        this.userId = userData.user_id;

        console.log('AISummaryCopilot: User ID loaded from localStorage:', this.userId);
      } catch (error) {
        console.error('Error accessing localStorage:', error);
      }
    },

    // Get JWT token from localStorage
    getJwtToken() {
      try {
        const storedToken = localStorage.getItem(this.localStorageTokenKey);
        if (storedToken) {
          return storedToken;
        }

        const rawTokenData = localStorage.getItem('token');
        if (rawTokenData) {
          if (rawTokenData.startsWith('{')) {
            const tokenData = JSON.parse(rawTokenData);
            return tokenData.token || tokenData.access_token || null;
          }
          if (rawTokenData.includes('token')) {
            return rawTokenData;
          }
        }

        console.warn('No JWT token found in localStorage');
        return null;
      } catch (error) {
        console.error('Error accessing localStorage for JWT token:', error);
        return null;
      }
    },

    // Connection methods
    connect() {
      // Get userId from localStorage if not already set
      if (!this.userId) {
        this.getUserIdFromLocalStorage();
      }

      // Get token
      const token = this.getJwtToken();

      if (!this.userId || !token) {
        console.error("AISummaryCopilot: User ID and JWT Token are required to connect");
        // this.$emit('error', "User ID or authentication token not found");
        // this.$emit('response-received', "User ID or authentication token not found. Please log in again to continue.");
        this.connectionStatus = 'error';
        return;
      }

      // Create WebSocket URL
      const wsUrl = this.serverUrl.endsWith('/')
        ? `${this.serverUrl}${this.userId}`
        : `${this.serverUrl}/${this.userId}`;

      try {
        // Close existing connection if any
        this.disconnect();

        console.log('AISummaryCopilot: Connecting to WebSocket server...');
        // this.$emit('status-update', 'Connecting to AI service...');

        // Create new WebSocket connection
        this.socket = new WebSocket(wsUrl);

        // Connection opened
        this.socket.addEventListener('open', this.handleSocketOpen);

        // Listen for messages
        this.socket.addEventListener('message', this.handleSocketMessage);

        // Connection closed
        this.socket.addEventListener('close', this.handleSocketClose);

        // Connection error
        this.socket.addEventListener('error', this.handleSocketError);

      } catch (error) {
        console.error('AISummaryCopilot: Error creating WebSocket:', error);
        this.connectionStatus = 'error';
        // this.$emit('error', `Error connecting to AI service: ${error.message}`);
        // this.$emit('response-received', `Error connecting to AI service: ${error.message}`);
      }
    },

    disconnect() {
      if (this.socket) {
        this.socket.close(1000, 'User disconnected');
        console.log('AISummaryCopilot: Disconnected from WebSocket');
      }
    },

    handleSocketOpen() {
      console.log('AISummaryCopilot: WebSocket connected');
      this.connectionStatus = 'initializing';
      this.isConnected = false;
      
      // this.$emit('status-update', 'Connected to AI service. Authenticating...');

      // Get token
      const token = this.getJwtToken();

      // Send JWT token for authentication
      this.socket.send(token);
    },

    handleSocketMessage(event) {
      console.log('AISummaryCopilot: Message from server:', event.data);

      try {
        const data = JSON.parse(event.data);

        // Always emit the raw message content for the parent to display
        if (data.message) {
          this.$emit('response-received', data.message);
        }

        // Update status
        if (data.status) {
          this.connectionStatus = data.status;
          
          if (data.status === 'connected') {
            this.isConnected = true;
            this.isProcessing = false;
            // this.$emit('status-update', 'Authentication successful. AI ready.');
          } else if (data.status === 'processing') {
            this.isProcessing = true;
            // this.$emit('processing');
            // Also emit the processing message to be displayed
            if (data.message) {
              this.$emit('response-received', data.message);
            }
          } else if (data.status === 'error') {
            this.isConnected = false;
            this.isProcessing = false;
            // this.$emit('error', data.message || 'Unknown error occurred');
            // Error message is already emitted as response-received above
          } else if (data.status === 'initializing') {
            this.isConnected = false;
            this.isProcessing = false;
          }
        }

        // Set processing to false when complete
        if (data.status === 'complete') {
          this.isProcessing = false;
        }

      } catch (error) {
        console.error('AISummaryCopilot: Error parsing message:', error);
        // If not JSON, just emit the raw text
        this.$emit('response-received', event.data);
      }
    },

    handleSocketClose(event) {
      console.log('AISummaryCopilot: WebSocket disconnected', event);
      this.connectionStatus = 'disconnected';
      this.isConnected = false;
      this.isProcessing = false;
      this.socket = null;
      
      const closeMessage = `Disconnected: ${event.reason || 'Connection closed'} (Code: ${event.code})`;
      // this.$emit('status-update', closeMessage);
      
      // For abnormal closures, also emit as a response to be displayed
      if (event.code !== 1000) {
        this.$emit('response-received', closeMessage);
      }
    },

    handleSocketError(event) {
      console.error('AISummaryCopilot: WebSocket error:', event);
      this.connectionStatus = 'error';
      this.isConnected = false;
      this.isProcessing = false;
      
      const errorMessage = 'Error connecting to AI service';
      // this.$emit('error', errorMessage);
      // this.$emit('response-received', errorMessage);
      console.log(errorMessage)
    },

    // Send a message to the AI
    sendMessage(message) {
      if (!message || message.trim() === '') {
        console.warn('AISummaryCopilot: Empty message, not sending');
        return;
      }

      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
        console.log('AISummaryCopilot: WebSocket is not connected. Attempting to reconnect...');
        // this.$emit('status-update', 'Reconnecting to AI service...');
        // this.$emit('response-received', 'Connecting to AI service...');
        
        // Connect and then send after connected
        this.connect();
        
        // Wait for connection before sending
        const checkAndSend = setInterval(() => {
          if (this.isConnected) {
            clearInterval(checkAndSend);
            this.sendMessageToSocket(message);
          }
        }, 500);
        
        // Set a timeout to prevent endless waiting
        setTimeout(() => {
          clearInterval(checkAndSend);
          if (!this.isConnected) {
            // const timeoutMessage = 'Could not connect to AI service. Please try again.';
            // this.$emit('error', timeoutMessage);
            // this.$emit('response-received', timeoutMessage);
          }
        }, 10000);
        
        return;
      }

      this.sendMessageToSocket(message);
    },
    
    sendMessageToSocket(message) {
      // Gather context information
      const context = {
        message: message
      };

      // Add optional context if provided
      if (this.projectId) context.project_id = parseInt(this.projectId, 10);
      if (this.talentId) context.talent_id = parseInt(this.talentId, 10);

      console.log('AISummaryCopilot: Sending message with context:', context);

      // Send message to server
      this.socket.send(JSON.stringify(context));

      // Set processing status
      this.isProcessing = true;
      // this.$emit('processing');
      // this.$emit('response-received', 'Processing your request... Please wait a moment.');
    },

    sendContext(context) {
      if(context && (context.message || context.prompt_template_type)) {
        console.log('AISummaryCopilot: Sending context:', context);
      } else {
        console.warn('AISummaryCopilot: No context to send');
        return;
      }

      if (!this.socket || this.socket.readyState !== WebSocket.OPEN || !this.isConnected) {
        console.warn('AISummaryCopilot: WebSocket is not connected. Cannot send context.');

        this.connect();
        const checkAndSend = setInterval(() => {
          if (this.isConnected) {
            clearInterval(checkAndSend);
            this.sendContext(context);
          }
        }, 500);
      }

      

      // Send context to server
      this.socket.send(JSON.stringify(context));
    }
  }
};
</script>